import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Check, Ellipsis, Link2, Pencil, PinOff } from "lucide-react";
import { isEmpty } from "lodash-es";

import { AppStateContext } from "@/state/AppStateContext";
import { removeAgentUser } from "@/api/api";
import { Agent } from "@/api/models";
import { changeAgent, closeSidebarOnMobile, doTrackEvent, refreshUserAgents } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface AgentListItemProps {
  agent: Agent;
}

const AgentListItem = ({ agent }: AgentListItemProps) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const handleAgentChange = (agent: Agent) => changeAgent(appStateContext, agent);
  const handleTrackEvent = (type: string, item: string, text: string, conversation_id: string, cgpt_id: string, agent_id: string) => doTrackEvent(type, item, text, conversation_id, cgpt_id, agent_id);
  const handleRefreshUserAgents = () => refreshUserAgents(appStateContext);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [removing, setRemoving] = useState<boolean>(false);
  
  let isSelected = agent.id === appStateContext?.state.currentAgent?.id && isEmpty(appStateContext?.state.currentChat);
  
  /**
   * Handle selecting an agent to chat with
  **/
  const handleSelectItem = () => {
    navigate('/');
    handleAgentChange(agent);
    closeSidebarOnMobile(appStateContext);
    handleTrackEvent('click', 'agent_open', 'Agent - Open', '', '', agent?.id || '');
  }

  /**
   * Handle sharing an agent
  **/
  const onShare = () => {
    setLinkCopied(true);
    navigator.clipboard.writeText(window.location.origin + "/agents/v/" + agent?.id);
    setTimeout(() => {
      setLinkCopied(false);
    }, 3000);
    handleTrackEvent('click', 'agent_share', 'Agent - Share', '', '', agent?.id || '');
  }
  
  /**
   * Handle removing agent from user's sidebar
   * 
   * @param {React.MouseEvent} e - The mouse click event
  **/
  const onRemove = async (e: React.MouseEvent) => {
    setIsPopoverOpen(false);
    e.stopPropagation();
    setRemoving(true);

    // Remove selected agent from current user
    await removeAgentUser(agent);

    // Refresh the user's agent list in sidebar
    handleRefreshUserAgents();

    setTimeout(() => {
      setRemoving(false);
    }, 2000);
    handleTrackEvent('click', 'agent_remove', 'Agent - Remove', '', '', agent?.id || '');
  }
  
  /** 
   * Handle navigating to open an agent for editing
   * 
   * @param {React.MouseEvent} e - The mouse click event
  **/
  const onEdit = (e: React.MouseEvent) => {
    setIsPopoverOpen(false);
    e.stopPropagation();
    navigate('/agents/e/' + agent.id);
    handleTrackEvent('click', 'agent_edit_sidebar', 'Agent - Edit (Sidebar)', '', '', agent?.id || '');
  }

  return (
    <div className={`group flex justify-between gap-2 w-full hover:bg-neutral ${isSelected ? `bg-neutral` : ``} rounded-lg cursor-pointer px-2 py-1.5 mb-0.5`} onClick={handleSelectItem}>
      <div className="flex items-center w-full min-w-0 hover:bg-neutral rounded-lg cursor-pointer select-none" title={agent.name}>
        <div className={`flex items-center justify-center w-[24px] h-[24px] rounded-full`}>
          <Bot size={18} />
        </div>
        <div className="flex-1 truncate dark:text-white ms-2">{agent.name}</div>
      </div>
      
      <div className="h-[24px]">
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger onClick={(e) => e.stopPropagation()}>
            {removing ? <span className="loading loading-spinner loading-xs mt-1"></span> : <div className="[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 hover:bg-base-100 rounded-lg cursor-pointer px-1 mt-0.5" onClick={() => handleTrackEvent('click', 'agent_menu', 'Agent - Menu', '', '', agent?.id || '')}><Ellipsis size={20} /></div>}
          </PopoverTrigger>
          <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[150px] dark:text-white p-3">
            <Dialog>
              <DialogTrigger className="w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="" className="-ms-0.5 icon"><path d="M2.66821 12.6663V12.5003C2.66821 12.1331 2.96598 11.8353 3.33325 11.8353C3.70052 11.8353 3.99829 12.1331 3.99829 12.5003V12.6663C3.99829 13.3772 3.9992 13.8707 4.03052 14.2542C4.0612 14.6298 4.11803 14.8413 4.19849 14.9993L4.2688 15.1263C4.44511 15.4137 4.69813 15.6481 5.00024 15.8021L5.13013 15.8577C5.2739 15.9092 5.46341 15.947 5.74536 15.97C6.12888 16.0014 6.62221 16.0013 7.33325 16.0013H12.6663C13.3771 16.0013 13.8707 16.0014 14.2542 15.97C14.6295 15.9394 14.8413 15.8825 14.9993 15.8021L15.1262 15.7308C15.4136 15.5545 15.6481 15.3014 15.802 14.9993L15.8577 14.8695C15.9091 14.7257 15.9469 14.536 15.97 14.2542C16.0013 13.8707 16.0012 13.3772 16.0012 12.6663V12.5003C16.0012 12.1332 16.2991 11.8355 16.6663 11.8353C17.0335 11.8353 17.3313 12.1331 17.3313 12.5003V12.6663C17.3313 13.3553 17.3319 13.9124 17.2952 14.3626C17.2624 14.7636 17.1974 15.1247 17.053 15.4613L16.9866 15.6038C16.7211 16.1248 16.3172 16.5605 15.8215 16.8646L15.6038 16.9866C15.227 17.1786 14.8206 17.2578 14.3625 17.2952C13.9123 17.332 13.3553 17.3314 12.6663 17.3314H7.33325C6.64416 17.3314 6.0872 17.332 5.63696 17.2952C5.23642 17.2625 4.87552 17.1982 4.53931 17.054L4.39673 16.9866C3.87561 16.7211 3.43911 16.3174 3.13501 15.8216L3.01294 15.6038C2.82097 15.2271 2.74177 14.8206 2.70435 14.3626C2.66758 13.9124 2.66821 13.3553 2.66821 12.6663ZM9.33521 12.5003V4.9388L7.13696 7.13704C6.87732 7.39668 6.45625 7.39657 6.19653 7.13704C5.93684 6.87734 5.93684 6.45631 6.19653 6.19661L9.52954 2.86263L9.6311 2.77962C9.73949 2.70742 9.86809 2.66829 10.0002 2.66829C10.1763 2.66838 10.3454 2.73819 10.47 2.86263L13.804 6.19661C14.0633 6.45628 14.0634 6.87744 13.804 7.13704C13.5443 7.39674 13.1222 7.39674 12.8625 7.13704L10.6653 4.93977V12.5003C10.6651 12.8673 10.3673 13.1652 10.0002 13.1654C9.63308 13.1654 9.33538 12.8674 9.33521 12.5003Z"></path></svg>
                  <div className="select-none text-sm">Share</div>
                </div>
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
                <DialogHeader>
                  <DialogTitle className="mb-3">Share link to {agent?.name || ''}</DialogTitle>
                  <DialogDescription>Copy the link to this agent in order to share it with others.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <div className="border border-secondary rounded-[15px] text-xs p-2">{window.location.origin + "/agents/v/" + agent?.id}</div>
                  <button type="button" className="btn btn-success" onClick={onShare}>{linkCopied ? <><Check /> Copied</> : <><Link2 /> Copy Link</>}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3" onClick={onRemove}>
              <PinOff size={16} />
              <div className="select-none text-sm">Remove</div>
            </div>
            {appStateContext?.state.currentUser?.id == agent.created_by ? 
              <div className="flex items-center gap-3 hover:bg-neutral rounded-xl cursor-pointer py-2 px-3" onClick={(e) => onEdit(e)}>
                <Pencil size={16} />
                <div className="select-none text-sm">Edit</div>
              </div>
            : null}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export default AgentListItem;
